/**
 * The page SDK — `window.showpilot` on the customer's page. Small on
 * purpose (it rides the main bundle) and additive to /v1: nothing here
 * changes for an embed that never calls it.
 *
 *   showpilot.on('tour.completed', h) / off   — the milestone vocabulary
 *                                              the webhooks use
 *   showpilot.identify({ email, userId, name, traits, signature, timestamp })
 *   showpilot.context({ plan: 'enterprise' })  — what the agent should know now
 *   showpilot.open({ tour, step }) / open()    — a tour, or the panel
 *   showpilot.open({ action })                 — an Action Link, by id
 *   showpilot.ready(cb)                        — after the widget mounted
 *
 * Calls made before the bundle loads queue on `window.showpilotq`
 * (`[method, ...args]`) and replay on install; calls that need the mount
 * (identify, open, context-on-a-session) queue until boot binds them.
 */

type Handler = (data: Record<string, unknown>) => void;

export interface IdentifyInput {
  email?: string;
  userId?: string;
  name?: string;
  traits?: Record<string, string>;
  /** `sha256=<hmac>` from the customer's server over `<userId|email>.<timestamp>`. */
  signature?: string;
  timestamp?: number;
}

export interface OpenInput {
  tour?: string;
  step?: number;
  /** An Action Link id the operator declared on the agent. */
  action?: string;
}

/** page = a button or the SDK; hash = a deep link; arrival = a rule the server matched on /v1/decide. */
export type ActionSource = 'page' | 'hash' | 'arrival';

export interface PageSdkBindings {
  publicId: string;
  apiBaseUrl: string;
  fingerprint: () => string;
  openPanel: () => void;
  openTour: (publicId: string, step?: number) => void;
  openAction: (id: string, source: ActionSource) => void;
}

export interface PageSdk {
  on: (event: string, handler: Handler) => () => void;
  off: (event: string, handler: Handler) => void;
  identify: (input: IdentifyInput) => Promise<{ ok: boolean; visitorId?: string; identity?: string }>;
  context: (data: Record<string, string>) => void;
  open: (input?: OpenInput) => void;
  ready: (cb: () => void) => void;
  /** Internal: the boot binds these once the mount resolved. */
  _bind: (b: PageSdkBindings) => void;
  /** Internal: the data-layer bridge hands every GTM milestone here. */
  _emit: (milestone: string, data: Record<string, unknown>) => void;
  _v: 1;
}

declare global {
  interface Window {
    showpilot?: PageSdk;
    showpilotq?: unknown[][];
  }
}

const handlers = new Map<string, Set<Handler>>();
let bindings: PageSdkBindings | null = null;
const pending: Array<() => void> = [];
let pageContext: Record<string, string> = {};
/** Universal-embed pages with deferred triggers: how to mount NOW for an Action. */
let mountOnDemand: (() => void) | null = null;
let actionWanted = false;

/** What every chat turn and the session create carry as `context.page`. */
export function getPageContext(): Record<string, string> | null {
  return Object.keys(pageContext).length > 0 ? { ...pageContext } : null;
}

/** GTM milestone names → the SDK/webhook vocabulary. Unknown names pass as `a.b`. */
const EVENT_NAMES: Record<string, string> = {
  agent_shown: 'agent.shown',
  conversation_started: 'conversation.started',
  content_viewed: 'content.viewed',
  content_completed: 'content.completed',
  voice_call_started: 'voice.started',
  lead_captured: 'lead.identified',
  meeting_booked: 'meeting.booked',
  tour_played: 'tour.played',
  tour_step: 'tour.step_viewed',
  tour_cta: 'tour.cta_clicked',
  tour_completed: 'tour.completed',
  tour_closed: 'tour.closed',
};

export function sdkEventName(milestone: string): string {
  return EVENT_NAMES[milestone] ?? milestone.replace('_', '.');
}

/** Called by the data-layer bridge for every milestone; handlers never break the widget. */
export function fireSdkEvent(name: string, data: Record<string, unknown>): void {
  for (const key of [name, '*']) {
    const set = handlers.get(key);
    if (!set) continue;
    for (const h of set) {
      try {
        h(key === '*' ? { ...data, event: name } : data);
      } catch (err) {
        console.warn('[showpilot] handler failed', err);
      }
    }
  }
}

function whenBound(fn: () => void): void {
  if (bindings) fn();
  else pending.push(fn);
}

/**
 * An Action reached the page (button, deep link or SDK). Before the
 * mount exists it waits like any SDK call — and, on a page whose widget
 * is waiting on a trigger, it IS the trigger: the mount happens now.
 */
export function requestAction(id: string, source: ActionSource): void {
  if (!bindings) {
    actionWanted = true;
    const m = mountOnDemand;
    mountOnDemand = null;
    m?.();
  }
  whenBound(() => bindings!.openAction(id, source));
}

/** The discover path registers this when triggers would defer the mount. */
export function setMountOnDemand(fn: (() => void) | null): void {
  if (fn && actionWanted && !bindings) fn();
  else mountOnDemand = fn;
}

async function postIdentify(input: IdentifyInput): Promise<{ ok: boolean; visitorId?: string; identity?: string }> {
  if (!bindings) return { ok: false };
  const body = JSON.stringify({ publicId: bindings.publicId, fingerprint: bindings.fingerprint(), ...input });
  const res = await fetch(`${bindings.apiBaseUrl}/v1/visitors/identify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  if (!res.ok) return { ok: false };
  const json = (await res.json()) as { visitorId?: string; identity?: string };
  fireSdkEvent('visitor.identified', { visitorId: json.visitorId, identity: json.identity });
  return { ok: true, ...json };
}

function createSdk(): PageSdk {
  const sdk: PageSdk = {
    _v: 1,
    on(event, handler) {
      let set = handlers.get(event);
      if (!set) handlers.set(event, (set = new Set()));
      set.add(handler);
      return () => sdk.off(event, handler);
    },
    off(event, handler) {
      handlers.get(event)?.delete(handler);
    },
    identify(input) {
      return new Promise((resolve) => {
        whenBound(() => {
          postIdentify(input ?? {}).then(resolve, () => resolve({ ok: false }));
        });
      });
    },
    context(data) {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(data ?? {})) {
        if (typeof k === 'string' && k.length <= 64 && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
          next[k] = String(v).slice(0, 200);
        }
      }
      pageContext = { ...pageContext, ...next };
    },
    open(input) {
      if (input?.action) {
        requestAction(String(input.action), 'page');
        return;
      }
      whenBound(() => {
        if (input?.tour) bindings!.openTour(input.tour, input.step);
        else bindings!.openPanel();
      });
    },
    ready(cb) {
      whenBound(cb);
    },
    _emit(milestone, data) {
      fireSdkEvent(sdkEventName(milestone), data);
    },
    _bind(b) {
      bindings = b;
      // Pages that gate their own fallback (a link that should navigate
      // when the agent never loaded) listen for this.
      try {
        document.dispatchEvent(new CustomEvent('showpilot:ready'));
      } catch {
        /* no CustomEvent */
      }
      const run = pending.splice(0);
      for (const fn of run) {
        try {
          fn();
        } catch (err) {
          console.warn('[showpilot] queued call failed', err);
        }
      }
    },
  };
  return sdk;
}

let ownSdk: PageSdk | null = null;

/**
 * Install `window.showpilot` once and replay the pre-load queue. Idempotent
 * for THIS bundle. A dev channel loads two bundles (the CDN loader, then
 * the channel build); each has its own copy of this module, and the one
 * that boots must own the global, or `showpilot.open` and the hash/click
 * paths bind to different copies and the calls queue forever.
 */
export function installPageSdk(): PageSdk {
  if (typeof window === 'undefined') return createSdk();
  if (ownSdk && window.showpilot === ownSdk) return ownSdk;
  const sdk = createSdk();
  ownSdk = sdk;
  const queued = Array.isArray(window.showpilotq) ? window.showpilotq.splice(0) : [];
  window.showpilot = sdk;
  window.showpilotq = { push: (call: unknown[]) => replay(sdk, call) } as unknown as unknown[][];
  for (const call of queued) replay(sdk, call);
  return sdk;
}

function replay(sdk: PageSdk, call: unknown[]): void {
  const [method, ...args] = call;
  const fn = (sdk as unknown as Record<string, unknown>)[String(method)];
  if (typeof fn === 'function' && !String(method).startsWith('_')) {
    try {
      (fn as (...a: unknown[]) => unknown).apply(sdk, args);
    } catch (err) {
      console.warn('[showpilot] queued call failed', err);
    }
  }
}

/** Boot calls this once the mount resolved. */
export function bindPageSdk(b: PageSdkBindings): void {
  installPageSdk()._bind(b);
}

export function resetPageSdkForTests(): void {
  handlers.clear();
  bindings = null;
  ownSdk = null;
  pending.length = 0;
  pageContext = {};
  mountOnDemand = null;
  actionWanted = false;
  if (typeof window !== 'undefined') {
    delete window.showpilot;
    delete window.showpilotq;
  }
}
